import React from 'react';
import { MdUpload } from 'react-icons/md';
import { MdPreview, MdEdit, MdDelete } from 'react-icons/md';

import { Box, Button, Flex, Icon, Text } from '@lucidhq/lucidium';
import {
  BackgroundColorProperty,
  BorderColorProperty
} from '@lucidhq/lucidium/lss/csstypes';

import { useFetchForcedExposureCreatives } from '~/components/Shared/hooks/useFetchForcedExposureCreatives';
import { t } from '~/utils/i18n';

import { CampaignManagerFooter } from '../CampaignManagerFooter';
import { useCampaignManager } from '../CampaignManagerProvider';
import { StepLayout } from '../Steps/StepLayout';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { PreviewDialog } from './PreviewDialog';
import {
  EXPOSURE_DEVICE,
  EXPOSURE_TYPE,
  UploadMediaDialog
} from './UploadMediaDialog/UploadMediaDialog';

export const MediaStep = () => {
  const {
    managedCampaign,
    goToStep,
    steps,
    activeStep,
    can
  } = useCampaignManager();

  const nextStepId = can.limitedEdit
    ? steps.REVIEW.id
    : steps[activeStep].nextId;
  const prevStepId = steps[activeStep].prevId;

  const { data, refetch } = useFetchForcedExposureCreatives(
    managedCampaign?.details.campaign_id
  );

  const showUploadButton =
    !data?.items || (!!data.items && data.items.length < 4);

  const uploadButtonLabel = t(
    `campaignManager.actions.${
      data?.items && data.items.length > 0
        ? 'uploadAdditionalCreative'
        : 'uploadCreative'
    }`
  );

  return (
    <StepLayout.Container>
      <StepLayout.Header title={t('campaignManager.media.header.title')} />
      <StepLayout.Content>
        <Box
          lss={{
            width: '100%'
          }}
        >
          <Flex lss={{ flexDirection: 'column', gap: '8px' }}>
            {(data?.items ?? []).map(creative => (
              <Flex
                key={creative.id}
                lss={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: '#F8F8F8' as BackgroundColorProperty,
                  borderRadius: 'md',
                  border: 'regular',
                  borderColor: '#E0E0E0' as BorderColorProperty
                }}
              >
                <Text>{creative.display_name}</Text>
                <Flex>
                  <PreviewDialog
                    campaignId={managedCampaign?.details.campaign_id}
                    creativeId={creative.id}
                  >
                    {({ handleToggle }) => (
                      <Button
                        data-testid="preview-creative-btn"
                        emphasis="low"
                        lss={{
                          borderRadius: 'rounded',
                          border: 'none'
                        }}
                        onClick={handleToggle}
                      >
                        <Icon
                          size={20}
                          as={MdPreview}
                          lss={{ color: 'black' }}
                        />
                      </Button>
                    )}
                  </PreviewDialog>

                  <UploadMediaDialog
                    refetchCreatives={refetch}
                    initValues={{
                      creativeId: creative.id,
                      displayName: creative.display_name,
                      exposureDevice: creative.exposure_device as EXPOSURE_DEVICE,
                      exposureType: creative.exposure_type as EXPOSURE_TYPE
                    }}
                    fileName={creative.file_name}
                    updatedAt={creative.updated_at}
                  >
                    {({ onToggle }) => (
                      <Button
                        data-testid="edit-creative-btn"
                        emphasis="low"
                        onClick={onToggle}
                        lss={{
                          borderRadius: 'rounded',
                          border: 'none'
                        }}
                      >
                        <Icon size={20} as={MdEdit} lss={{ color: 'black' }} />
                      </Button>
                    )}
                  </UploadMediaDialog>

                  <DeleteConfirmDialog
                    campaignId={managedCampaign?.details.campaign_id}
                    creativeId={creative.id}
                    onDelete={() => refetch()}
                  >
                    {({ handleDelete }) => (
                      <Button
                        data-testid="delete-creative-btn"
                        emphasis="low"
                        lss={{
                          borderRadius: 'rounded',
                          border: 'none'
                        }}
                        onClick={handleDelete}
                      >
                        <Icon
                          size={20}
                          as={MdDelete}
                          lss={{ color: 'black' }}
                        />
                      </Button>
                    )}
                  </DeleteConfirmDialog>
                </Flex>
              </Flex>
            ))}
          </Flex>

          {showUploadButton && (
            <UploadMediaDialog refetchCreatives={refetch}>
              {({ onToggle }) => (
                <Button
                  data-testid="upload-creative-btn"
                  emphasis="medium"
                  onClick={onToggle}
                  lss={{
                    borderRadius: 'md',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '25px'
                  }}
                >
                  <Icon
                    size={24}
                    as={MdUpload}
                    lss={{ color: 'secondary500' }}
                  />
                  {uploadButtonLabel}
                </Button>
              )}
            </UploadMediaDialog>
          )}

          <CampaignManagerFooter
            onBack={() => prevStepId && goToStep(prevStepId)}
            skipProps={
              !data?.items.length
                ? {
                    onClick: () => nextStepId && goToStep(nextStepId)
                  }
                : undefined
            }
            saveContinueProps={
              !!data?.items.length
                ? {
                    onClick: () => nextStepId && goToStep(nextStepId)
                  }
                : undefined
            }
          />
        </Box>
      </StepLayout.Content>
    </StepLayout.Container>
  );
};
